import type { PlayerId } from '../../shared/types/ids';

/**
 * Who a player's *durable* learning belongs to (R1-3, SKL-05, D-025).
 *
 * `PlayerRuntimeState.playerId` is `hero-1` or `hero-2`: it names a **seat**, for one sitting.
 * Lanes are fixed for a mission so that the resume guard can match slot for slot and the summary
 * can list one row per hero — all session-scoped jobs, all correct.
 *
 * Skill evidence, skill estimates and game progress are none of those things. They outlive the
 * sitting, so keying them by the seat means the seat accumulates a history: Ada plays in the right
 * lane on Monday, and on Tuesday her brother sits there and is served content adapted to her, has
 * her badge, and adds his answers to her record. A guest — who by construction keeps nothing
 * (D-025) — inherits whichever named child last used that seat.
 *
 * So durable learning is keyed by the profile, and a player with no profile has no durable
 * learning: nothing is read for them and nothing is written about them. The session-scoped
 * identity is untouched — participation counts, the resume guard, the summary rows and every
 * runtime event still speak in lanes.
 *
 * Returns `null` for a guest, which callers must read as "skip storage entirely" rather than as
 * "store under an empty key".
 */
export function learningSubjectIdOf(player: {
  readonly playerId: PlayerId;
  readonly profileId?: PlayerId;
  readonly isGuest?: boolean;
}): PlayerId | null {
  if (player.isGuest === true) return null;
  return player.profileId ?? null;
}
